import { useCallback, useEffect, useRef, useState } from 'react';

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 120; // buffer for the mouse to travel from trigger to card

export interface UseEntityHoverResult {
  triggerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  anchor: HTMLElement | null;
  /** Spread onto the trigger element */
  onTriggerMouseEnter: () => void;
  onTriggerMouseLeave: () => void;
  /** Spread onto the card element to keep it alive while hovered */
  onCardMouseEnter: () => void;
  onCardMouseLeave: () => void;
}

/**
 * Manages hover state for entity popover triggers.
 *
 * - Opens after OPEN_DELAY_MS to avoid flicker on quick passes.
 * - Closes after CLOSE_DELAY_MS on mouse leave (trigger OR card), so the
 *   mouse can travel from the trigger into the card without it disappearing.
 */
export function useEntityHover(): UseEntityHoverResult {
  const triggerRef = useRef<HTMLElement>(null as unknown as HTMLElement);
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearOpen = useCallback(() => {
    if (openTimerRef.current !== null) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, CLOSE_DELAY_MS);
  }, [cancelClose]);

  const onTriggerMouseEnter = useCallback(() => {
    cancelClose();
    clearOpen();
    openTimerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        setAnchor(triggerRef.current);
        setIsOpen(true);
      }
    }, OPEN_DELAY_MS);
  }, [cancelClose, clearOpen]);

  const onTriggerMouseLeave = useCallback(() => {
    clearOpen();
    scheduleClose();
  }, [clearOpen, scheduleClose]);

  const onCardMouseEnter = useCallback(() => {
    cancelClose();
  }, [cancelClose]);

  const onCardMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  useEffect(() => {
    return () => {
      clearOpen();
      cancelClose();
    };
  }, [clearOpen, cancelClose]);

  return {
    triggerRef,
    isOpen,
    anchor,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
  };
}
